import { useMemo, useState } from "react";
import { NumField } from "@/components/tool/CalcFields";
import { ppi } from "@/lib/design/color";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function PpiCalc() {
  const locale = getLocale();
  const [width, setWidth] = useState("1920");
  const [height, setHeight] = useState("1080");
  const [diagonal, setDiagonal] = useState("15.6");

  const out = useMemo(() => {
    const w = Number(width);
    const h = Number(height);
    const d = Number(diagonal);
    if (![w, h, d].every(Number.isFinite)) return null;
    const value = ppi(w, h, d);
    if (value == null) return null;
    return { ppi: value, dotPitch: 25.4 / value };
  }, [width, height, diagonal]);

  const num = (n: number, d = 1) =>
    n.toLocaleString(locale, { maximumFractionDigits: d });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-3">
        <NumField
          id="width"
          label={m.ppi_width()}
          value={width}
          onChange={setWidth}
          suffix="px"
        />
        <NumField
          id="height"
          label={m.ppi_height()}
          value={height}
          onChange={setHeight}
          suffix="px"
        />
        <NumField
          id="diagonal"
          label={m.ppi_diagonal()}
          value={diagonal}
          onChange={setDiagonal}
          suffix="in"
        />
      </div>
      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.ppi_label()}
            </span>
            <span
              data-testid="ppi-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {num(out.ppi, 1)} PPI
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.ppi_dot_pitch(), value: `${num(out.dotPitch, 3)} mm` },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.ppi_hint()}</p>
      )}
    </div>
  );
}
